import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Stack,
  Grid,
  Card,
  CardContent,
  Divider,
  TextField,
  Button,
  Tooltip,
} from '@mui/material';
import {
  getParentLatencyMs,
  getCapLatencyMs,
  getLatencyChipColor,
  getLatencyLabel,
  formatLatencyMs,
  formatLatencyTooltip,
  DEFAULT_PARENT_LATENCY_ALERT_MS,
} from '../services/latency';
import { MAP_UI } from '../theme/mapViewUi';
import { formatDeviceLabel } from '../utils/deviceDisplayUtils';

/** Good/success metrics: green border only, no filled neon chip. */
function alertMetricChipProps(muiColor, extraSx = {}) {
  if (muiColor === 'success') {
    return {
      variant: 'outlined',
      sx: {
        fontWeight: 'bold',
        bgcolor: 'transparent',
        color: MAP_UI.onlineChipText,
        border: '1px solid',
        borderColor: MAP_UI.deviceOnlineBorder,
        ...extraSx,
      },
    };
  }
  return {
    color: muiColor,
    variant: 'outlined',
    sx: { fontWeight: 'bold', ...extraSx },
  };
}

// Note: the prop is now a millisecond threshold for the median round-trip
// latency to the device's parent. We keep the prop name for backwards
// compatibility with the parent <AlertsPage> wiring.
const AlertsView = ({
  devices = [],
  isLoading,
  healthThreshold,
  setHealthThreshold,
  txFailedThreshold,
  setTxFailedThreshold,
  latencyThresholdMs,
  setLatencyThresholdMs,
}) => {
  const navigate = useNavigate();

  // Local state for input values (before applying)
  const [inputHealthThreshold, setInputHealthThreshold] = useState(healthThreshold);
  const [inputTxFailedThreshold, setInputTxFailedThreshold] = useState(txFailedThreshold);
  const [inputLatencyThresholdMs, setInputLatencyThresholdMs] = useState(latencyThresholdMs);

  useEffect(() => { setInputHealthThreshold(healthThreshold); }, [healthThreshold]);
  useEffect(() => { setInputTxFailedThreshold(txFailedThreshold); }, [txFailedThreshold]);
  useEffect(() => { setInputLatencyThresholdMs(latencyThresholdMs); }, [latencyThresholdMs]);

  const handleDeviceClick = (serial) => {
    navigate(`/device/${serial}`);
  };

  const handleApplyFilters = () => {
    setHealthThreshold(inputHealthThreshold);
    setTxFailedThreshold(inputTxFailedThreshold);
    setLatencyThresholdMs(inputLatencyThresholdMs);
  };

  const hasUnappliedChanges = () => {
    return inputHealthThreshold !== healthThreshold ||
           inputTxFailedThreshold !== txFailedThreshold ||
           inputLatencyThresholdMs !== latencyThresholdMs;
  };

  // Filter devices with poor backhaul health, high TX failure rate,
  // or excessive median round-trip latency to parent.
  const alertDevices = devices.filter(device => {
    const hasPoorTxRatio = device.backhaul_tx_ratio !== undefined &&
                          device.backhaul_tx_ratio !== null &&
                          device.backhaul_tx_ratio < healthThreshold;

    const hasHighTxFailedPercentage = device.backhaul_tx_failed_percentage !== undefined &&
                                     device.backhaul_tx_failed_percentage !== null &&
                                     device.backhaul_tx_failed_percentage > txFailedThreshold;

    const parentMs = getParentLatencyMs(device);
    const hasHighParentLatency = parentMs !== null && parentMs > latencyThresholdMs;

    return hasPoorTxRatio || hasHighTxFailedPercentage || hasHighParentLatency;
  });

  const getTxRatioColor = (ratio) => {
    if (ratio >= 0.8) return 'success';
    if (ratio >= 0.6) return 'warning';
    return 'error';
  };

  const getTxRatioLabel = (ratio) => {
    if (ratio >= 0.8) return 'Good';
    if (ratio >= 0.6) return 'Fair';
    return 'Poor';
  };

  const getTxFailedColor = (percentage) => {
    if (percentage <= 0.1) return 'success';
    if (percentage <= 0.3) return 'warning';
    return 'error';
  };

  const getTxFailedLabel = (percentage) => {
    if (percentage <= 0.1) return 'Good';
    if (percentage <= 0.3) return 'Fair';
    return 'Poor';
  };

  const getOverallHealthColor = (device) => {
    const txRatioColor = device.backhaul_tx_ratio !== undefined && device.backhaul_tx_ratio !== null ?
      getTxRatioColor(device.backhaul_tx_ratio) : 'success';
    const txFailedColor = device.backhaul_tx_failed_percentage !== undefined && device.backhaul_tx_failed_percentage !== null ?
      getTxFailedColor(device.backhaul_tx_failed_percentage) : 'success';
    const parentMs = getParentLatencyMs(device);
    const capMs = getCapLatencyMs(device);
    const worstLatencyMs = (parentMs === null && capMs === null)
      ? null
      : Math.max(parentMs ?? 0, capMs ?? 0);
    const latencyColor = worstLatencyMs === null ? 'success' : getLatencyChipColor(worstLatencyMs);

    if (txRatioColor === 'error' || txFailedColor === 'error' || latencyColor === 'error') return 'error';
    if (txRatioColor === 'warning' || txFailedColor === 'warning' || latencyColor === 'warning') return 'warning';
    return 'success';
  };

  // Render a chip pair for one latency target (parent or CAP).
  const renderLatencyRow = (device, target) => {
    const summary = device.latency?.[target];
    const ms = target === 'to_parent' ? getParentLatencyMs(device) : getCapLatencyMs(device);
    if (ms === null) return null;
    const color = getLatencyChipColor(ms);
    const label = target === 'to_parent' ? 'Parent:' : 'CAP:';
    const tooltipLabel = target === 'to_parent' ? 'Median round-trip to immediate parent' : 'Median round-trip to CAP (root)';
    return (
      <Box key={target} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
          {label}
        </Typography>
        <Tooltip title={formatLatencyTooltip(summary, tooltipLabel)}>
          <Chip
            label={formatLatencyMs(ms)}
            size="small"
            {...alertMetricChipProps(color, { cursor: 'help' })}
          />
        </Tooltip>
        <Chip
          label={getLatencyLabel(ms)}
          size="small"
          {...alertMetricChipProps(color)}
        />
      </Box>
    );
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Network Health Alerts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Devices with poor TX ratio, high TX failure percentage, or high parent latency
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isLoading && (
            <CircularProgress size={20} />
          )}
        </Box>
      </Box>

      {/* Health Threshold Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
          Alert Thresholds (OR Logic - Show devices meeting ANY threshold)
        </Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={2.5}>
            <TextField
              size="small"
              type="number"
              label="TX Ratio Threshold"
              value={inputHealthThreshold}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setInputHealthThreshold(value);
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    setInputHealthThreshold(numValue);
                  }
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value === '' || isNaN(parseFloat(value))) {
                  setInputHealthThreshold(0.7);
                }
              }}
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              fullWidth
              helperText="Alert if TX ratio < threshold"
            />
          </Grid>
          <Grid item xs={12} sm={2.5}>
            <TextField
              size="small"
              type="number"
              label="TX Failed Threshold"
              value={inputTxFailedThreshold}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setInputTxFailedThreshold(value);
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    setInputTxFailedThreshold(numValue);
                  }
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value === '' || isNaN(parseFloat(value))) {
                  setInputTxFailedThreshold(0.3);
                }
              }}
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              fullWidth
              helperText="Alert if TX failed > threshold"
            />
          </Grid>
          <Grid item xs={12} sm={2.5}>
            <TextField
              size="small"
              type="number"
              label="Parent Latency (ms)"
              value={inputLatencyThresholdMs}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setInputLatencyThresholdMs(value);
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    setInputLatencyThresholdMs(numValue);
                  }
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value === '' || isNaN(parseFloat(value))) {
                  setInputLatencyThresholdMs(DEFAULT_PARENT_LATENCY_ALERT_MS);
                }
              }}
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              helperText="Alert if median latency to parent > threshold"
            />
          </Grid>
          <Grid item xs={12} sm={2.5} sx={{ display: 'flex', alignItems: 'flex-start', pt: '8px !important' }}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={!hasUnappliedChanges()}
              fullWidth
              sx={{ height: '40px' }}
            >
              Apply Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {alertDevices.length} device{alertDevices.length !== 1 ? 's' : ''} with issues
              </Typography>
              {hasUnappliedChanges() && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                  Unapplied changes
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Alert Devices */}
      {alertDevices.length === 0 ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          No devices meet alert criteria. All devices are operating within thresholds.
        </Alert>
      ) : (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Devices Requiring Attention ({alertDevices.length})
          </Typography>
          <Grid container spacing={2}>
          {alertDevices.map((device) => (
            <Grid item xs={12} md={6} lg={4} key={device.serial_number}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                  },
                  transition: 'box-shadow 0.3s ease-in-out',
                  border: '2px solid',
                  borderColor: getOverallHealthColor(device) + '.light'
                }}
                onClick={() => handleDeviceClick(device.serial_number.toLowerCase())}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: device.connected ? MAP_UI.deviceOnlineBorder : '#f44336',
                        marginRight: 2,
                      }}
                    />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {formatDeviceLabel(device)}
                    </Typography>
                    <Chip
                      label={getOverallHealthColor(device) === 'error' ? 'Critical' : 'Warning'}
                      size="small"
                      color={getOverallHealthColor(device)}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Backhaul Health
                    </Typography>

                    {device.backhaul_tx_ratio !== undefined && device.backhaul_tx_ratio !== null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                          TX Ratio:
                        </Typography>
                        <Chip
                          label={`${(device.backhaul_tx_ratio * 100).toFixed(1)}%`}
                          size="small"
                          {...alertMetricChipProps(getTxRatioColor(device.backhaul_tx_ratio))}
                        />
                        <Chip
                          label={getTxRatioLabel(device.backhaul_tx_ratio)}
                          size="small"
                          {...alertMetricChipProps(getTxRatioColor(device.backhaul_tx_ratio))}
                        />
                      </Box>
                    )}

                    {device.backhaul_tx_failed_percentage !== undefined && device.backhaul_tx_failed_percentage !== null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                          TX Failed:
                        </Typography>
                        <Chip
                          label={`${(device.backhaul_tx_failed_percentage * 100).toFixed(1)}%`}
                          size="small"
                          {...alertMetricChipProps(getTxFailedColor(device.backhaul_tx_failed_percentage))}
                        />
                        <Chip
                          label={getTxFailedLabel(device.backhaul_tx_failed_percentage)}
                          size="small"
                          {...alertMetricChipProps(getTxFailedColor(device.backhaul_tx_failed_percentage))}
                        />
                      </Box>
                    )}

                    {renderLatencyRow(device, 'to_parent')}
                    {renderLatencyRow(device, 'to_cap')}
                  </Box>

                  {/* Device Info */}
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Serial:</Typography>
                      <Typography variant="caption">{device.serial_number}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Firmware:</Typography>
                      <Typography variant="caption">{device.firmware || 'N/A'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Parent:</Typography>
                      <Typography variant="caption">{device.parent_serial || 'None'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Last Seen:</Typography>
                      <Typography variant="caption">
                        {device.last_seen ? new Date(device.last_seen).toLocaleString() : 'N/A'}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    )}
  </Paper>
  );
};

export default AlertsView;
